/**
 * ListSummaryFooter — compact section rendered below EntityForm
 * in non-edit mode. Surfaces:
 *   - Activity row (latest event summary, links to /activity drill-down)
 *   - Inheritance row (counts of incoming/outgoing template links)
 *
 * Both rows render only when relevant.
 */

import { useUserMap } from '@/features/users/users.hooks';
import { CircularProgress } from '@/features/lists/components/CircularProgress';
import { relativeTime } from '@/lib/time';
import {
  Check,
  CheckCircle2,
  ChevronRight,
  GitBranch,
  History,
} from 'lucide-react';
import type { List, ListEvent } from '@certhon/models';
import type { ReactNode } from 'react';

interface ListSummaryFooterProps {
  list: List;
  /** Progress { answered, total }. Omit to hide the progress row. */
  progress?: { answered: number; total: number };
  /** Called when user taps activity link. Optional — omit to hide row. */
  onActivityClick?: () => void;
  /** Called when user taps inheritance link. Optional — omit to hide row. */
  onInheritanceClick?: () => void;
}

/** Name Footer is poorly choosen. this is a set of custom readonly-fields that extend the list entityform. */
export function ListSummaryFooter({
  list,
  progress,
  onActivityClick,
  onInheritanceClick,
}: ListSummaryFooterProps) {
  const userMap = useUserMap('recnum');
  const lastActivity = getLastActivity(list.events ?? [], userMap);

  const incoming = list.dependencies.length;
  const outgoing = list.dependants.length;
  const hasInheritance = incoming > 0 || outgoing > 0;

  if (!onActivityClick && !onInheritanceClick && !progress) return null;

  return (
    <div
      className="bg-muted/40 border-border -mx-2 -mt-1 space-y-1 border-t px-2 pt-1 pb-2 text-sm
        md:-mx-4 md:-mt-3 md:px-4 md:pt-1 md:pb-4"
    >
      {progress && (
        <Row icon={CheckCircle2} label="Progress">
          {list.status === 'closed' ? (
            <span className="flex items-center gap-2">
              <span className="bg-foreground flex h-5 w-5 items-center justify-center rounded-full">
                <Check className="text-background h-3 w-3" strokeWidth={3} />
              </span>
              <span className="text-muted-foreground text-xs">
                Completed · {progress.answered}/{progress.total}
              </span>
            </span>
          ) : (
            <CircularProgress
              value={progress.answered}
              max={progress.total}
              size={20}
              strokeWidth={2.5}
            />
          )}
        </Row>
      )}
      {onActivityClick && (
        <Row icon={History} label="Activity">
          <SummaryLink onClick={onActivityClick}>
            {lastActivity ? (
              <>
                {lastActivity.text}{' '}
                <span className="text-muted-foreground">
                  {lastActivity.timeAgo}
                </span>
              </>
            ) : (
              <span className="text-muted-foreground">view all</span>
            )}
          </SummaryLink>
        </Row>
      )}

      {onInheritanceClick && (
        <Row icon={GitBranch} label="Inheritance">
          {hasInheritance ? (
            <SummaryLink onClick={onInheritanceClick}>
              <span className="text-muted-foreground">
                {[
                  incoming > 0 && `${incoming} incoming`,
                  outgoing > 0 && `${outgoing} outgoing`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </SummaryLink>
          ) : (
            <span className="text-muted-foreground">none</span>
          )}
        </Row>
      )}
    </div>
  );
}

function SummaryLink({
  onClick,
  children,
}: {
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-foreground group/link flex items-center gap-1 text-left"
    >
      <span>{children}</span>
      <ChevronRight
        className="text-muted-foreground group-hover/link:text-foreground h-3.5 w-3.5 shrink-0
          transition-colors"
      />
    </button>
  );
}

function Row({
  icon: Icon,
  label,
  children,
}: {
  icon: typeof History;
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="flex items-center gap-2 py-0.5">
      <Icon className="text-muted-foreground h-3.5 w-3.5 shrink-0" />
      <span className="text-muted-foreground w-20 shrink-0 text-xs">
        {label}
      </span>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

function getLastActivity(
  events: ListEvent[],
  userMap: Map<number, { name: string }>,
): { text: string; timeAgo: string } | null {
  if (events.length === 0) return null;
  const sorted = [...events].sort(
    (a, b) => b.created.getTime() - a.created.getTime(),
  );
  const latest = sorted[0];
  const user = userMap.get(latest.user_id);
  const userName = user?.name ?? `User ${latest.user_id}`;

  // Count consecutive PATCH events by same user (e.g. answered N questions)
  let count = 0;
  for (const e of sorted) {
    if (e.user_id !== latest.user_id) break;
    if (e.type === 'PATCH') count++;
    else break;
  }

  const timeAgo = relativeTime(latest.created);

  // ???: why are we not leveraging the timeline events? expectatin is that this 'last activity' corresponds with the timeline it links to
  const text =
    count > 0
      ? `${userName} answered ${count} question${count === 1 ? '' : 's'}`
      : `${userName} ${
          latest.type === 'CREATE_LIST'
            ? 'created the list'
            : 'performed an action'
        }`;

  return { text, timeAgo };
}
